import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Inspection, Project } from '../types'
import { fmtDate } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

const STATUS_COLOR: Record<Inspection['status'], BadgeColor> = {
  not_scheduled: 'gray',
  scheduled: 'gold',
  pass: 'green',
  fail: 'red',
  conditional: 'gold',
}

type PrepResult = Awaited<ReturnType<typeof api.inspectionPrep.generate>>

export default function InspectionsTab({ project }: { project: Project }) {
  const [items, setItems] = useState<Inspection[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<Inspection>>({ type: 'city' })
  const [prep, setPrep] = useState<Record<string, PrepResult | null>>({})
  const [prepBusy, setPrepBusy] = useState<string | null>(null)
  const [prepError, setPrepError] = useState<string | null>(null)

  useEffect(() => {
    api.inspections.list(project.id).then(setItems)
  }, [project.id])

  async function generatePrep(id: string) {
    setPrepBusy(id)
    setPrepError(null)
    try {
      const r = await api.inspectionPrep.generate(id)
      setPrep((p) => ({ ...p, [id]: r }))
    } catch (e) {
      setPrepError((e as Error).message)
    } finally {
      setPrepBusy(null)
    }
  }
  async function loadCachedPrep(id: string) {
    if (prep[id]) return
    try {
      const r = await api.inspectionPrep.get(id)
      if (r.prep) {
        setPrep((p) => ({
          ...p,
          [id]: {
            checklist: r.prep!.checklist,
            preexisting_walk_flags_to_address: r.prep!.recent_walk_flags,
            scheduling_notes: '',
          },
        }))
      }
    } catch {
      /* skip */
    }
  }

  async function add() {
    if (!draft.inspection_name) return
    const ins = await api.inspections.create(project.id, draft)
    setItems((p) => [...p, ins])
    setAdding(false)
    setDraft({ type: 'city' })
  }

  async function setStatus(id: string, status: Inspection['status']) {
    const updated = await api.inspections.update(id, {
      status,
      ...(status === 'pass' || status === 'fail' || status === 'conditional'
        ? { completed_date: new Date().toISOString().slice(0, 10) }
        : {}),
    })
    setItems((p) => p.map((x) => (x.id === id ? updated : x)))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Inspections</h2>
          <p className="muted small">
            {items.length} total · {items.filter((i) => i.status === 'pass').length} passed
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Schedule'}
        </button>
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Name</span>
            <input
              value={draft.inspection_name ?? ''}
              onChange={(e) => setDraft({ ...draft, inspection_name: e.target.value })}
              placeholder="Foundation inspection"
            />
          </label>
          <label className="form-row">
            <span>Phase</span>
            <input
              value={draft.phase ?? ''}
              onChange={(e) => setDraft({ ...draft, phase: e.target.value })}
              placeholder="Foundation & Slab"
            />
          </label>
          <label className="form-row">
            <span>Type</span>
            <select
              value={draft.type ?? 'city'}
              onChange={(e) =>
                setDraft({ ...draft, type: e.target.value as Inspection['type'] })
              }
            >
              <option value="city">City</option>
              <option value="third_party">Third Party</option>
              <option value="heritage_internal">Heritage Internal</option>
            </select>
          </label>
          <label className="form-row">
            <span>Scheduled date</span>
            <input
              type="date"
              value={draft.scheduled_date ?? ''}
              onChange={(e) => setDraft({ ...draft, scheduled_date: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Inspector</span>
            <input
              value={draft.inspector_name ?? ''}
              onChange={(e) => setDraft({ ...draft, inspector_name: e.target.value })}
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add Inspection
          </button>
        </div>
      )}

      {items.map((i) => (
        <div key={i.id} className="card mb-2">
          <div className="row-between">
            <div>
              <div className="row-title">{i.inspection_name}</div>
              <div className="muted small">
                {i.phase ?? '—'} · {i.type.replace('_', ' ')} ·{' '}
                {i.scheduled_date ? `scheduled ${fmtDate(i.scheduled_date)}` : 'unscheduled'}
              </div>
              {i.completed_date && (
                <div className="muted small">Completed {fmtDate(i.completed_date)}</div>
              )}
              {i.corrections && (
                <div className="danger small mt-2">Corrections: {i.corrections}</div>
              )}
            </div>
            <div className="text-right">
              <Badge color={STATUS_COLOR[i.status]}>{i.status.toUpperCase().replace('_', ' ')}</Badge>
            </div>
          </div>
          {i.status !== 'pass' && (
            <div className="row gap-2 mt-2">
              <button className="btn-ghost small" onClick={() => setStatus(i.id, 'scheduled')}>
                Scheduled
              </button>
              <button className="btn-ghost small" onClick={() => setStatus(i.id, 'pass')}>
                Pass
              </button>
              <button className="btn-ghost small" onClick={() => setStatus(i.id, 'fail')}>
                Fail
              </button>
              <button className="btn-ghost small" onClick={() => setStatus(i.id, 'conditional')}>
                Conditional
              </button>
              <button
                className="btn-ghost small"
                onClick={() => generatePrep(i.id)}
                disabled={prepBusy === i.id}
                title="AI-generate a pre-inspection checklist"
              >
                {prepBusy === i.id ? 'Generating…' : prep[i.id] ? 'Regenerate prep' : 'Generate prep checklist'}
              </button>
              {!prep[i.id] && (
                <button
                  className="btn-ghost small"
                  onClick={() => loadCachedPrep(i.id)}
                  title="Show previously generated checklist"
                >
                  Load saved
                </button>
              )}
            </div>
          )}
          {prep[i.id] && (
            <div className="card mt-2" style={{ background: 'rgba(212,175,55,0.06)' }}>
              <strong>Pre-inspection checklist</strong>
              {prep[i.id]!.scheduling_notes && (
                <p className="muted small">{prep[i.id]!.scheduling_notes}</p>
              )}
              <ul>
                {prep[i.id]!.checklist.map((c, idx) => (
                  <li key={idx} className="small">
                    <span
                      style={{
                        background:
                          c.priority === 'must_fix'
                            ? '#cc4444'
                            : c.priority === 'should_fix'
                            ? '#d4af37'
                            : '#666',
                        color: '#fff',
                        fontSize: '0.65rem',
                        padding: '1px 5px',
                        borderRadius: 2,
                        marginRight: 6,
                      }}
                    >
                      {c.priority}
                    </span>
                    {c.item}{' '}
                    <span className="muted" style={{ fontSize: '0.72rem' }}>
                      ({c.source})
                    </span>
                  </li>
                ))}
              </ul>
              {prep[i.id]!.preexisting_walk_flags_to_address.length > 0 && (
                <details>
                  <summary className="muted small" style={{ cursor: 'pointer' }}>
                    Recent site-walk flags ({prep[i.id]!.preexisting_walk_flags_to_address.length})
                  </summary>
                  <ul>
                    {prep[i.id]!.preexisting_walk_flags_to_address.map((f, idx) => (
                      <li key={idx} className="small">
                        [{f.severity}] {f.item} (walk {f.walk_date})
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      ))}
      {prepError && <div className="card alert mt-2">{prepError}</div>}
    </section>
  )
}
